import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton, IonButton } from '@ionic/react';
import { Box, Typography, Chip, Button } from '@mui/material';
// import { getRequisitionDetails } from '../services/requisitionService';
import { getRequisitionDetails } from '../../../api/requisition/requisition';

interface RequisitionDetails {
  id: string;
  requisition_id: string;
  title: string;
  description: string;
  amount: number;
  status: string;
  expense_date: string;
  category: string;
  attachment_path: string;
  manager_notes: string;
  postponed_to: string;
}

const RequisitionDetails: React.FC = () => {
  // const { id } = useParams<{ id: string }>();

  const location = useLocation()
      const history = useHistory()
    
      // Extract job ID from URL path
      const pathSegments = location.pathname.split("/")
      const id = pathSegments[pathSegments.length - 1]


  const [requisition, setRequisition] = useState<RequisitionDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRequisition = async () => {
      try {
        const data = await getRequisitionDetails(id);
        console.log('Fetched requisition:', data);
        setRequisition(data);
      } catch (error) {
        console.error('Error fetching requisition:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRequisition();
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'postponed': return 'warning';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  if (!requisition) {
    return (
      <IonPage>
        <IonContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Typography>Requisition not found</Typography>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/my-requisitions" />
          </IonButtons>
          <IonTitle>Requisition Details</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {requisition.title}
          </Typography>
          <Chip 
            label={requisition.status} 
            color={getStatusColor(requisition.status)} 
            sx={{ mb: 2 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Description:</strong>
          </Typography>
          <Typography paragraph>
            {requisition.description}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography><strong>Amount:</strong> ${requisition.amount.toFixed(2)}</Typography>
          <Typography><strong>Expense Date:</strong> {new Date(requisition.expense_date).toLocaleDateString()}</Typography>
          <Typography><strong>Category:</strong> {requisition.category}</Typography>
          {requisition.postponed_to && (
            <Typography><strong>Postponed To:</strong> {new Date(requisition.postponed_to).toLocaleDateString()}</Typography>
          )}
        </Box>

        {requisition.manager_notes && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Manager Notes:</strong>
            </Typography>
            <Typography>
              {requisition.manager_notes}
            </Typography>
          </Box>
        )}

        {requisition.attachment_path && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Attachment:</strong>
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => window.open(requisition.attachment_path, '_blank')}
            >
              View Attachment
            </Button>
          </Box>
        )}
      </IonContent>
    </IonPage>
  );
};

export default RequisitionDetails;